import React from "react";
import styled from "styled-components";
import { Repeater, types } from "react-bricks/frontend";
import { InfoCardProps } from "./InfoCard";
import { color } from "styles/colours";
import {
  getVerticalPadding,
  VerticalPadding,
  VerticalPaddingWrapper,
  verticalPadSideProp,
} from "../utils/verticalPadding";
import TextHeading from "../common/TextHeading";

interface InfoCardsSectionProps {
  background?: types.IColor;
  gap: number;
  cardHeightOffset?: number;
  cards: types.RepeaterItems<InfoCardProps>;
  paddingVertical?: VerticalPadding;
  imageType: "icon" | "cover" | "none";
  size?: "s" | "m";
  showHeading?: boolean;
  heading?: types.TextValue;
}

export const InfoCardSection: types.Brick<InfoCardsSectionProps> = ({
  cards,
  gap,
  background,
  paddingVertical,
  imageType,
  showHeading,
  heading,
  cardHeightOffset = 0,
  size = "m",
}) => {
  return (
    <VerticalPaddingWrapper paddingVertical={paddingVertical} backgroundColor={background?.color}>
      <Wrapper>
        {showHeading && <TextHeading padBottom={true} propName="heading" value={heading} />}
        <InfoCardsWrapper gap={gap} padTop={showHeading}>
          <Repeater
            propName="cards"
            items={cards}
            itemProps={{ imageType, size, cardHeightOffset }}
          />
        </InfoCardsWrapper>
      </Wrapper>
    </VerticalPaddingWrapper>
  );
};

InfoCardSection.schema = {
  name: "cards-section",
  label: "Info Cards",
  category: "Product Cards Components",
  previewImageUrl: "/static/bricks/product-cards.png",
  // Define repeaters for child bricks
  repeaterItems: [
    {
      name: "cards",
      itemType: "info-card",
      itemLabel: "Info Cards",
      min: 1,
      max: 20,
    },
  ],
  // Define default props
  getDefaultProps: () => ({
    background: { color: color.white },
    gap: 2,
    paddingVertical: "s",
    cardHeightOffset: 0,
    size: "m",
    imageType: "icon",
    cards: [
      {
        title: "Some info card",
        content:
          "Covers the cost of high-quality healthcare, quickly, if employees were to get ill. Provided by Bupa.",
        hasLink: true,
        linkText: "Learn more",
        linkUrl: "#",
        imageType: "icon",
      },
      {
        title: "Another info card",
        content:
          "Covers the cost of high-quality healthcare, quickly, if employees were to get ill. Provided by Bupa.",
        hasLink: true,
        linkText: "Learn more",
        linkUrl: "#",
        imageType: "icon",
      },
      {
        title: "Last info card!",
        content:
          "Covers the cost of high-quality healthcare, quickly, if employees were to get ill. Provided by Bupa.",
        hasLink: true,
        linkText: "Learn more",
        linkUrl: "#",
        imageType: "icon",
      },
    ],
  }),
  stories: [
    {
      id: "info-card-section-covers",
      name: "Photo cards",
      showAsBrick: true,
      previewImageUrl: "/static/bricks/cover-cards.png",
      props: {
        background: { color: color.white },
        gap: 2,
        paddingVertical: "s",
        imageType: "cover",
        showHeading: true,
        heading: "Speakers",
        size: "m",
        cards: [
          {
            title: "Fred Baker",
            content: "Brother of the fashion mogal and influencer Teddy.",
            hasLink: true,
            linkText: "Find out more",
            linkUrl: "#",
            imageType: "cover",
            image: {
              src: "https://dummyimage.com/350x200/f3f4f6/9ca3af",
            },
          },
          {
            title: "Poddy McPodcaster",
            content: "Real life podcaster and influencer.",
            hasLink: true,
            linkText: "Find out more",
            linkUrl: "#",
            imageType: "cover",
            image: {
              src: "https://dummyimage.com/350x200/f3f4f6/9ca3af",
            },
          },
          {
            title: "Testy McTestface",
            content: "World's most famous test pilot.",
            hasLink: true,
            linkText: "Find out more",
            linkUrl: "#",
            imageType: "cover",
            image: {
              src: "https://dummyimage.com/350x200/f3f4f6/9ca3af",
            },
          },
        ],
      },
    },
  ],
  // Define side edit props
  sideEditProps: [
    {
      name: "background",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "White", value: { color: color.white } },
          { label: "Gray", value: { color: color.B210 } },
        ],
      },
    },
    {
      name: "gap",
      label: "Gap",
      type: types.SideEditPropType.Number,
      shouldRefreshStyles: true,
    },
    {
      name: "size",
      label: "Size (width)",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Radio,
        options: [
          { label: "Small", value: "s" },
          { label: "Medium", value: "m" },
        ],
      },
    },
    {
      name: "cardHeightOffset",
      label: "Adjust card height",
      type: types.SideEditPropType.Range,
      shouldRefreshStyles: true,
      rangeOptions: {
        min: -15,
        max: 15,
        step: 1,
      },
    },
    {
      name: "imageType",
      label: "Image type",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Radio,
        options: [
          { label: "Icon", value: "icon" },
          { label: "Cover", value: "cover" },
          { label: "None", value: "none" },
        ],
      },
    },
    {
      name: "showHeading",
      label: "Show heading?",
      type: types.SideEditPropType.Boolean,
    },
    verticalPadSideProp,
  ],
};

export const InfoCardsWrapper = styled.section<{
  gap: number;
  padTop: boolean;
}>`
  display: flex;
  gap: ${(p) => p.gap}rem;
  padding-top: ${(p) => (p.padTop ? "2rem" : 0)};
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  row-gap: 2rem;
`;

const Wrapper = styled.div`
  padding: 0 5%;
`;
